import React, { useState } from 'react';
import { X, RotateCcw, CheckCircle, AlertTriangle, Loader2, Target, Shield } from 'lucide-react';
import { toast } from '../../utils/toastUtils';
import { remediationTracking } from '../../services/api/client';

const SEVERITY_COLOR = {
  critical: 'text-red-400 bg-red-500/15 border-red-500/30',
  high:     'text-orange-400 bg-orange-500/15 border-orange-500/30',
  medium:   'text-yellow-400 bg-yellow-500/15 border-yellow-500/30',
  low:      'text-blue-400 bg-blue-500/15 border-blue-500/30',
  info:     'text-gray-400 bg-gray-500/15 border-gray-500/30',
};

export default function RescanModal({ vuln, onClose, onDone }) {
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [result, setResult] = useState(null);

  const handleRescan = async () => {
    setStatus('loading');
    try {
      const data = await remediationTracking.triggerRescan(vuln.id);
      setResult(data);
      setStatus('success');
      // After success, tell parent the new status
      if (onDone) onDone(vuln.id, 'fixed');
      toast.success('Re-scan launched successfully');
    } catch (err) {
      setStatus('error');
      toast.error('Erreur lors du lancement du re-scan');
    }
  };

  const sevCls = SEVERITY_COLOR[vuln.severity] || SEVERITY_COLOR.info;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-brand-black-light rounded-2xl border border-gray-700 w-full max-w-lg shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/15 rounded-lg border border-blue-500/30">
              <RotateCcw className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h2 className="text-base font-bold text-white">Verification re-scan</h2>
              <p className="text-xs text-gray-400 mt-0.5">Verify whether the vulnerability has been fixed</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-700 rounded-lg transition-colors">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-4">
          {/* Vuln info */}
          <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-4 space-y-3">
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm font-medium text-gray-200">{vuln.title}</p>
              <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-xs border ${sevCls}`}>
                {vuln.severity}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="flex items-center gap-1.5 text-gray-400">
                <Target className="w-3.5 h-3.5" />
                <span className="font-mono">{vuln.host_ip || '—'}</span>
              </div>
              {vuln.cve_id && (
                <div className="flex items-center gap-1.5 text-gray-400">
                  <Shield className="w-3.5 h-3.5" />
                  <span>{vuln.cve_id}</span>
                </div>
              )}
            </div>
          </div>

          {/* Process description */}
          {status === 'idle' && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-gray-300">Ce que va faire le re-scan :</p>
              <div className="space-y-1.5 text-xs text-gray-400">
                {[
                  `Targeted nmap scan on ${vuln.host_ip || 'the target'}`,
                  vuln.cve_id ? `Checking for the presence of ${vuln.cve_id}` : 'Detecting vulnerability indicators',
                  'Automatic update: "Verified" if fixed, "In progress" if still present',
                ].map((step, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <span className="w-4 h-4 rounded-full bg-blue-500/20 text-blue-400 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                    <span>{step}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Loading */}
          {status === 'loading' && (
            <div className="flex flex-col items-center gap-3 py-4">
              <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
              <p className="text-sm text-gray-400">Lancement du re-scan en cours...</p>
            </div>
          )}

          {/* Success */}
          {status === 'success' && result && (
            <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 space-y-2">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <p className="text-sm font-medium text-green-400">Re-scan launched successfully</p>
              </div>
              <div className="text-xs text-gray-400 space-y-1">
                <p>Cible : <span className="font-mono text-gray-300">{result.target}</span></p>
                <p>ID du scan : <span className="font-mono text-gray-300 text-xs">{result.rescan_scan_id}</span></p>
                <p className="text-yellow-400 mt-2">
                  The vulnerability has been set to "To verify" status. The status will be updated automatically when the scan completes.
                </p>
              </div>
            </div>
          )}

          {/* Error */}
          {status === 'error' && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
              <p className="text-sm text-red-400">Error launching re-scan. Please try again.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-5 border-t border-gray-700">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm transition-colors">
            {status === 'success' ? 'Fermer' : 'Annuler'}
          </button>
          {status === 'idle' && (
            <button
              onClick={handleRescan}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              Lancer le re-scan
            </button>
          )}
          {status === 'error' && (
            <button
              onClick={handleRescan}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              Retry
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
